import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import { useGroupAssets, type GroupCylinderRow } from '@/hooks/useGroupAssets';
import {
  ALL_STORES,
  ChainPage,
  EmptyRow,
  StoreCell,
  StoreFilterRow,
  Tile,
} from '@/components/chain/ChainShell';

/**
 * /cylinders for a chain's corporate login — the CO₂ and gas balance at every
 * store, and what the chain is paying to rent them.
 *
 * This is the surface the merge helps most: cylinder balances are derived per
 * account from that account's own invoice history, so corporate could not see
 * a single tank without switching store 26 times.
 */
export default function ChainCylinders() {
  const { data, isLoading } = useGroupAssets();
  const [store, setStore] = useState<string>(ALL_STORES);
  const [term, setTerm] = useState('');

  const rows = useMemo(() => {
    let out = data?.cylinders ?? [];
    if (store !== ALL_STORES) out = out.filter((r) => r.customer_id === store);
    const q = term.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        [r.label, r.store_name, r.store_code].filter(Boolean).some((v) =>
          String(v).toLowerCase().includes(q),
        ),
      );
    }
    // Store, then gas type — read down a store's row block, not across types.
    return [...out].sort(
      (a, b) => a.store_name.localeCompare(b.store_name) || a.label.localeCompare(b.label),
    );
  }, [data, store, term]);

  /** Chain-wide count per gas type — the number worth negotiating on. */
  const byType = useMemo(() => {
    const m = new Map<string, { label: string; balance: number; rent: number }>();
    for (const r of data?.cylinders ?? []) {
      const cur = m.get(r.label) ?? { label: r.label, balance: 0, rent: 0 };
      cur.balance += Math.max(0, r.balance);
      cur.rent += Math.max(0, r.monthly_rent);
      m.set(r.label, cur);
    }
    return [...m.values()].sort((a, b) => b.balance - a.balance);
  }, [data]);

  const t = data?.totals;
  const brand = data?.master?.group_brand || data?.master?.name || 'your stores';
  const storesWithTanks = new Set(
    (data?.cylinders ?? []).filter((r) => r.balance > 0).map((r) => r.customer_id),
  ).size;

  return (
    <ChainPage
      title="Cylinders — all stores"
      intro={
        <>
          Every CO₂ and gas cylinder we have on rent across {brand}, by store. Counts
          come from each store's own delivery and pickup history.
        </>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Tile label="Cylinders out" value={isLoading ? '—' : String(t?.cylinders_out ?? 0)} />
        <Tile
          label="Monthly rent"
          value={isLoading ? '—' : formatUsd(t?.cylinder_monthly_rent ?? 0)}
        />
        <Tile
          label="Stores with tanks"
          value={isLoading ? '—' : String(storesWithTanks)}
          sub={t ? `of ${t.stores} on the account` : undefined}
        />
        <Tile label="Gas types" value={isLoading ? '—' : String(byType.length)} />
      </div>

      {byType.length > 0 && (
        <div className="rounded-2xl border border-brix-border bg-white px-5 py-4 mb-6">
          <p className="text-[11px] uppercase tracking-wide text-brix-muted font-semibold mb-2">
            Across the chain
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {byType.map((b) => (
              <span key={b.label} className="text-sm text-brix-ink">
                <span className="font-semibold tabular-nums">{b.balance}</span>{' '}
                <span className="text-brix-muted">{b.label}</span>
                {b.rent > 0 && (
                  <span className="text-brix-muted"> · {formatUsd(b.rent)}/mo</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <StoreFilterRow
        stores={data?.stores ?? []}
        store={store}
        onStore={setStore}
        term={term}
        onTerm={setTerm}
        searchPlaceholder="Gas type, store…"
      />

      <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brix-surface-alt/60 text-left text-[11px] uppercase tracking-wide text-brix-muted">
              <tr>
                <th className="px-4 py-3 font-semibold">Store</th>
                <th className="px-4 py-3 font-semibold">Gas</th>
                <th className="px-4 py-3 font-semibold text-right">On hand</th>
                <th className="px-4 py-3 font-semibold text-right">Rate</th>
                <th className="px-4 py-3 font-semibold text-right">Monthly</th>
                <th className="px-4 py-3 font-semibold">Counted from</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <EmptyRow colSpan={6}>
                  <Loader2 size={16} className="animate-spin inline-block mr-2" />
                  Adding up every store's cylinders…
                </EmptyRow>
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={6}>
                  {(data?.cylinders ?? []).length === 0
                    ? 'No cylinder activity on record for these stores yet.'
                    : 'Nothing matches those filters.'}
                </EmptyRow>
              ) : (
                rows.map((r: GroupCylinderRow) => (
                  <tr
                    key={`${r.customer_id}:${r.label}`}
                    className="border-t border-brix-border/70"
                  >
                    <StoreCell row={r} />
                    <td className="px-4 py-3 text-brix-ink">{r.label}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-brix-ink">
                      {r.balance}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-brix-muted">
                      {r.unit_price > 0 ? formatUsd(r.unit_price) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-brix-ink">
                      {r.monthly_rent > 0 ? formatUsd(r.monthly_rent) : '—'}
                    </td>
                    <td className="px-4 py-3 text-brix-muted">
                      {r.btrf_doc_number
                        ? `${r.btrf_doc_number}${r.btrf_date ? ` · ${fmtMirrorDate(r.btrf_date)}` : ''}`
                        : 'deliveries since first order'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-brix-muted">
        Counts run from each store's last rental invoice, plus deliveries and minus
        pickups since. If one looks wrong, a driver audit at that store re-counts it.
      </p>
    </ChainPage>
  );
}
